import { useState, useCallback, useContext } from "react";
import { PublicKey } from "@solana/web3.js";
import { useAnchorWallet, useConnection, useWallet } from "@solana/wallet-adapter-react";
import { AppConfigContext } from "../context";
import { WithdrawalTicket } from "../helpers/dataStructures";
import {
  buildBatchBurnWithdrawalInstructions,
  BurnWithdrawalAccounts,
} from "../helpers/buildBurnWithdrawalInstructions";
import { BATCH_CLAIM_CU } from "../helpers/batchClaim";
import { sendTx } from "./utils/utils";
import mixpanel from "mixpanel-browser";

export const useBurnWithdrawal = () => {
  const { connection } = useConnection();
  const { publicKey, sendTransaction } = useWallet();
  const wallet = useAnchorWallet();
  const {
    environment,
    priorityFeeLevel,
    vaultProgramAddress,
    vaultConfigAddress,
    kyrosFeeWalletAddress,
    jitoFeeWalletAddress,
  } = useContext(AppConfigContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAccounts = useCallback(
    (
      vrtMintAddress: string,
      supportedTokenMintAddress: string,
      vaultAddress: string
    ): BurnWithdrawalAccounts | null => {
      if (!publicKey) return null;
      return {
        staker: publicKey,
        vrtMint: new PublicKey(vrtMintAddress),
        supportedTokenMint: new PublicKey(supportedTokenMintAddress),
        vault: new PublicKey(vaultAddress),
        vaultConfig: new PublicKey(vaultConfigAddress),
        vaultProgram: new PublicKey(vaultProgramAddress),
        feeWallet: new PublicKey(kyrosFeeWalletAddress),
        restakingFeeWallet: new PublicKey(jitoFeeWalletAddress),
      };
    },
    [
      publicKey,
      vaultConfigAddress,
      vaultProgramAddress,
      kyrosFeeWalletAddress,
      jitoFeeWalletAddress,
    ]
  );

  const burnWithdrawal = useCallback(
    async (
      vrtMintAddress: string,
      supportedTokenMintAddress: string,
      vaultAddress: string,
      ticketInfo: WithdrawalTicket | null
    ) => {
      if (!wallet || !publicKey) {
        setError("Please connect your wallet.");
        return false;
      }
      if (!ticketInfo) {
        setError("Please select a ticket to claim.");
        return false;
      }

      const accounts = getAccounts(vrtMintAddress, supportedTokenMintAddress, vaultAddress);
      if (!accounts) {
        setError("Please connect your wallet.");
        return false;
      }

      mixpanel.track("Burn Withdrawal", {
        amount: ticketInfo.vrtAmount,
        wallet: wallet.publicKey?.toBase58(),
      });

      setLoading(true);
      setError(null);

      try {
        const ixs = await buildBatchBurnWithdrawalInstructions(accounts, [ticketInfo]);
        return await sendTx(
          environment,
          connection,
          publicKey,
          sendTransaction,
          ixs,
          priorityFeeLevel,
          [],
          BATCH_CLAIM_CU.BATCH_COMPUTE_LIMIT
        );
      } catch (err) {
        setError(`Something went wrong with the transaction: ${err}`);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [
      wallet,
      publicKey,
      getAccounts,
      environment,
      connection,
      sendTransaction,
      priorityFeeLevel,
    ]
  );

  const burnWithdrawalBatch = useCallback(
    async (
      vrtMintAddress: string,
      supportedTokenMintAddress: string,
      vaultAddress: string,
      tickets: WithdrawalTicket[]
    ) => {
      if (!wallet || !publicKey) {
        setError("Please connect your wallet.");
        return false;
      }
      if (tickets.length === 0) {
        setError("No tickets to claim.");
        return false;
      }

      const accounts = getAccounts(vrtMintAddress, supportedTokenMintAddress, vaultAddress);
      if (!accounts) {
        setError("Please connect your wallet.");
        return false;
      }

      mixpanel.track("Burn Withdrawal Batch", {
        count: tickets.length,
        wallet: wallet.publicKey?.toBase58(),
      });

      setLoading(true);
      setError(null);

      try {
        const ixs = await buildBatchBurnWithdrawalInstructions(accounts, tickets);
        return await sendTx(
          environment,
          connection,
          publicKey,
          sendTransaction,
          ixs,
          priorityFeeLevel,
          [],
          BATCH_CLAIM_CU.BATCH_COMPUTE_LIMIT
        );
      } catch (err) {
        setError(`Something went wrong with the transaction: ${err}`);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [
      wallet,
      publicKey,
      getAccounts,
      environment,
      connection,
      sendTransaction,
      priorityFeeLevel,
    ]
  );

  return { burnWithdrawal, burnWithdrawalBatch, loading, error };
};
